import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "@/lib/navigation";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Brand } from "@/components/brand";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@/components/ui/primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toaster";
import { getMe, superadminLogin, tenantAdminLogin } from "@/lib/api/endpoints";
import type { LoginResult, OrganizationChoice } from "@/types/api";

const superSchema = z.object({
  email: z.string().min(3, "Enter your email"),
  password: z.string().min(1, "Enter your password"),
});
// No tenant field: the server resolves the organisation from the credentials,
// and asks only when they open more than one. Requiring an admin to type the
// slug of an org they are already a member of was the thing being fixed.
const tenantSchema = superSchema;

/** Shared post-login routing: confirm the session, then forced change first. */
export function useAfterLogin() {
  const router = useRouter();
  return async function afterLogin(result: LoginResult) {
    const me = await getMe().catch(() => null);
    const mustChange = me?.must_change_password ?? result.must_change_password;
    router.replace(mustChange ? "/change-password" : "/dashboard");
    router.refresh();
  };
}

// Codes the Google callback appends as `?sso_error=` when it sends the browser
// back to a login page.
const SSO_ERRORS: Record<string, string> = {
  cancelled: "Google sign-in was cancelled.",
  unavailable: "Google sign-in is not enabled for this organisation.",
  domain_not_allowed: "That Google account's email domain is not allowed for this organisation.",
  no_access:
    "This Google account does not have access to any organisation. Ask your administrator to add your email.",
  expired: "Your Google sign-in expired. Please try again.",
  rate_limited: "Too many attempts. Please wait a few minutes and try again.",
  login_unavailable: "Sign-in is temporarily unavailable. Please try again shortly.",
  failed: "Google sign-in failed. Please try again or contact your administrator.",
};

/** Notices carried on the URL: an expired session, or a failed Google sign-in. */
export function LoginNotices() {
  // Read from the URL to avoid the Suspense requirement that useSearchParams
  // imposes on static pages.
  const [notice, setNotice] = React.useState<string | null>(null);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sso = params.get("sso_error");
    if (sso) setNotice(SSO_ERRORS[sso] ?? SSO_ERRORS.failed ?? null);
    else if (params.get("reason") === "expired")
      setNotice("Your session expired. Please sign in again.");
  }, []);
  if (!notice) return null;
  return (
    <p className="mb-4 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs text-foreground">
      {notice}
    </p>
  );
}

export default function LoginPage() {
  const afterLogin = useAfterLogin();

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Brand variant="stacked" />
        </div>
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
            <CardDescription>Aviary control plane &amp; your organisation.</CardDescription>
          </CardHeader>
          <CardContent>
            <LoginNotices />
            <Tabs defaultValue="superadmin">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="superadmin">Super-admin</TabsTrigger>
                <TabsTrigger value="tenant">Organisation</TabsTrigger>
              </TabsList>
              <TabsContent value="superadmin">
                <SuperadminForm onDone={afterLogin} />
              </TabsContent>
              <TabsContent value="tenant">
                <TenantForm onDone={afterLogin} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

function SuperadminForm({ onDone }: { onDone: (r: LoginResult) => void }) {
  const form = useForm<z.infer<typeof superSchema>>({
    resolver: zodResolver(superSchema),
    defaultValues: { email: "", password: "" },
  });
  const mutation = useMutation({
    mutationFn: (v: z.infer<typeof superSchema>) => superadminLogin(v.email, v.password),
    onSuccess: onDone,
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <form className="space-y-4 pt-2" onSubmit={form.handleSubmit((v) => mutation.mutate(v))}>
      <Field label="Email" error={form.formState.errors.email?.message}>
        <Input type="email" autoComplete="username" {...form.register("email")} />
      </Field>
      <Field label="Password" error={form.formState.errors.password?.message}>
        <Input type="password" autoComplete="current-password" {...form.register("password")} />
      </Field>
      <Button type="submit" className="w-full" disabled={mutation.isPending}>
        {mutation.isPending ? "Signing in…" : "Sign in as super-admin"}
      </Button>
    </form>
  );
}

export function TenantForm({ onDone }: { onDone: (r: LoginResult) => void }) {
  const form = useForm<z.infer<typeof tenantSchema>>({
    resolver: zodResolver(tenantSchema),
    defaultValues: { email: "", password: "" },
  });

  // Set when the credentials opened several organisations. The login is not
  // complete at that point — no cookie was issued — so we hold the entered
  // values and post again once one is picked.
  const [choices, setChoices] = React.useState<OrganizationChoice[] | null>(null);

  const mutation = useMutation({
    mutationFn: ({ tenant_slug }: { tenant_slug?: string }) => {
      const { email, password } = form.getValues();
      return tenantAdminLogin(email, password, tenant_slug);
    },
    onSuccess: (r) => {
      if (r.choices?.length) {
        setChoices(r.choices);
        return;
      }
      onDone(r);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (choices) {
    return (
      <div className="space-y-3 pt-2">
        <p className="text-sm text-muted-foreground">
          This sign-in opens more than one organisation. Choose one.
        </p>
        <div className="space-y-2">
          {choices.map((org) => (
            <Button
              key={org.slug}
              variant="outline"
              className="w-full justify-between"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ tenant_slug: org.slug })}
            >
              <span>{org.display_name}</span>
              <span className="font-mono text-xs text-muted-foreground">{org.slug}</span>
            </Button>
          ))}
        </div>
        <Button
          variant="ghost"
          className="w-full"
          onClick={() => setChoices(null)}
          disabled={mutation.isPending}
        >
          Use a different account
        </Button>
      </div>
    );
  }

  return (
    <form className="space-y-4 pt-2" onSubmit={form.handleSubmit(() => mutation.mutate({}))}>
      <Field label="Email" error={form.formState.errors.email?.message}>
        <Input type="email" autoComplete="username" {...form.register("email")} />
      </Field>
      <Field label="Password" error={form.formState.errors.password?.message}>
        <Input type="password" autoComplete="current-password" {...form.register("password")} />
      </Field>
      <Button type="submit" className="w-full" disabled={mutation.isPending}>
        {mutation.isPending ? "Signing in…" : "Sign in"}
      </Button>
    </form>
  );
}

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
